/**
 * DM voice-call signaling state machine + socket helpers.
 *
 * On top of the existing voice/SFU infrastructure (which uses
 * channelId as the SFU room key), DM calls add a ring/accept/reject
 * handshake so the callee can decline without the caller's audio
 * actually reaching them.
 *
 * Lifecycle:
 *   IDLE
 *     → user clicks "Позвонить" on the DM header
 *     → emit 'voice:dm:invite' { channelId }
 *     → set state OUTGOING
 *   OUTGOING (caller is ringing)
 *     → server emits 'voice:dm:rejected' → state DECLINED, then IDLE
 *     → server emits 'voice:dm:accepted' → state CONNECTING
 *     → user cancels → emit 'voice:dm:cancel' → state IDLE
 *     → 60s timeout → emit 'voice:dm:cancel' → state IDLE
 *   INCOMING (callee receives 'voice:dm:incoming')
 *     → server emits 'voice:dm:cancelled' (caller hung up) → state IDLE
 *     → user accepts → emit 'voice:dm:accept' + voice:join → state CONNECTING
 *     → user rejects → emit 'voice:dm:reject' → state IDLE
 *   CONNECTING / IN_CALL
 *     → user hangs up → voice:leave → state IDLE
 *
 * The state is a window-level singleton (one active call max for the
 * whole tab — multiple concurrent ring popovers would be UX chaos). UI
 * components subscribe via subscribeDmCall().
 */

import { getSocket } from '@/lib/socket';
import { joinVoice, leaveVoice } from '@/lib/voiceCommands';
import { primeAudioContext } from '@/lib/voice/audioGraph';
import { toast } from '@/lib/toast';
import { humanizeDmInviteError } from '@/components/contentPane/voiceErrors';

export type DmCallState =
  | { kind: 'idle' }
  | {
      kind: 'outgoing';
      channelId: string;
      /** Display info for the modal "Звоним <name>…". */
      target: { displayName: string; avatarUrl: string | null };
      startedAt: number;
    }
  | {
      kind: 'incoming';
      channelId: string;
      caller: {
        id: string;
        username: string;
        displayName: string | null;
        avatarUrl: string | null;
      };
      receivedAt: number;
    }
  | { kind: 'connecting'; channelId: string }
  | { kind: 'in-call'; channelId: string };

type Listener = (state: DmCallState) => void;

let current: DmCallState = { kind: 'idle' };
const listeners = new Set<Listener>();
let outgoingTimeout: ReturnType<typeof setTimeout> | null = null;
let incomingTimeout: ReturnType<typeof setTimeout> | null = null;

const OUTGOING_TIMEOUT_MS = 60_000;
// Slightly longer than the caller's own auto-cancel, so in the normal case
// their `voice:dm:cancel` lands first and this timer never fires. It exists for
// the case where the cancellation can never arrive — the caller's tab was
// closed or killed. The server now also cancels on the caller's disconnect;
// this is the last line of defence (e.g. server restarted mid-ring), because a
// ring modal you cannot dismiss without reloading is the worst outcome.
const INCOMING_TIMEOUT_MS = 65_000;

function set(next: DmCallState) {
  current = next;
  for (const fn of listeners) fn(next);
}

function clearIncomingTimer() {
  if (incomingTimeout) clearTimeout(incomingTimeout);
  incomingTimeout = null;
}

function clearOutgoingTimer() {
  if (outgoingTimeout) clearTimeout(outgoingTimeout);
  outgoingTimeout = null;
}

export function getDmCall(): DmCallState {
  return current;
}

export function subscribeDmCall(fn: Listener): () => void {
  listeners.add(fn);
  fn(current);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * Start an outgoing call. Caller-side only — sends invite to server,
 * starts the 60s auto-cancel timer.
 */
export function startOutgoingCall(
  channelId: string,
  target: { displayName: string; avatarUrl: string | null },
): void {
  if (current.kind !== 'idle') return;
  // The caller joins voice LATER, from the socket 'accepted' event — no user
  // activation there. Resume the mic-processing AudioContext now, inside the
  // "call" click, or the caller's processed track goes out silent (autoplay
  // policy) until they interact again.
  primeAudioContext();
  const socket = getSocket();
  set({
    kind: 'outgoing',
    channelId,
    target,
    startedAt: Date.now(),
  });
  clearOutgoingTimer();
  outgoingTimeout = setTimeout(() => {
    if (current.kind === 'outgoing' && current.channelId === channelId) {
      cancelOutgoingCall();
    }
  }, OUTGOING_TIMEOUT_MS);
  socket.emit('voice:dm:invite', { channelId }, (ack: { ok: boolean; error?: string }) => {
    if (!ack?.ok && current.kind === 'outgoing' && current.channelId === channelId) {
      clearOutgoingTimer();
      set({ kind: 'idle' });
      toast.error(humanizeDmInviteError(ack?.error));
    }
  });
}

export function cancelOutgoingCall(): void {
  if (current.kind !== 'outgoing') return;
  const socket = getSocket();
  socket.emit('voice:dm:cancel', { channelId: current.channelId });
  clearOutgoingTimer();
  set({ kind: 'idle' });
}

export function acceptIncomingCall(): void {
  if (current.kind !== 'incoming') return;
  const socket = getSocket();
  const channelId = current.channelId;
  socket.emit('voice:dm:accept', { channelId });
  clearIncomingTimer();
  set({ kind: 'connecting', channelId });
  // Use the canonical join path: joinVoice() sets the voice INTENT before
  // emitting voice:join. Without the intent, VoiceAudioLayer refuses to wire
  // up the SFU media (it gates on hasVoiceIntent), so a raw socket emit made
  // the server think we joined while no audio ever connected — the "answer
  // does nothing" bug.
  joinVoice(channelId, {
    onError: (msg) => {
      toast.error(msg);
      if (current.kind === 'connecting' && current.channelId === channelId) {
        set({ kind: 'idle' });
      }
    },
  });
}

export function rejectIncomingCall(): void {
  if (current.kind !== 'incoming') return;
  const socket = getSocket();
  socket.emit('voice:dm:reject', { channelId: current.channelId });
  clearIncomingTimer();
  set({ kind: 'idle' });
}

/**
 * Called by the socket listener when the server tells us about a
 * lifecycle event.
 */
export function onServerEvent(
  type: 'incoming' | 'cancelled' | 'rejected' | 'accepted' | 'state',
  payload: Record<string, unknown>,
): void {
  if (type === 'incoming') {
    // Don't override an active outgoing call — a race where both sides
    // ring each other at the same time isn't worth handling for v1; we
    // just keep our outgoing.
    if (current.kind !== 'idle') return;
    const channelId = typeof payload.channelId === 'string' ? payload.channelId : '';
    const raw = payload.caller;
    if (
      !channelId ||
      !raw ||
      typeof raw !== 'object' ||
      typeof (raw as Record<string, unknown>).id !== 'string' ||
      typeof (raw as Record<string, unknown>).username !== 'string'
    ) return;
    const caller = raw as { id: string; username: string; displayName: string | null; avatarUrl: string | null };
    set({
      kind: 'incoming',
      channelId,
      caller,
      receivedAt: Date.now(),
    });
    clearIncomingTimer();
    incomingTimeout = setTimeout(() => {
      // Give up on a ring nobody can end. No 'reject' is emitted: the caller is
      // gone by definition, and a decline would post a misleading system
      // message. This is a missed call.
      if (current.kind === 'incoming' && current.channelId === channelId) {
        set({ kind: 'idle' });
      }
    }, INCOMING_TIMEOUT_MS);
    return;
  }
  if (type === 'cancelled') {
    if (current.kind === 'incoming') {
      clearIncomingTimer();
      set({ kind: 'idle' });
    }
    return;
  }
  if (type === 'rejected') {
    if (current.kind === 'outgoing') {
      clearOutgoingTimer();
      set({ kind: 'idle' });
    }
    return;
  }
  if (type === 'accepted') {
    if (current.kind === 'outgoing') {
      clearOutgoingTimer();
      const channelId = current.channelId;
      set({ kind: 'connecting', channelId });
      // Caller joins voice once the callee accepts — via joinVoice() so the
      // voice INTENT is set and VoiceAudioLayer actually wires the SFU media
      // (a raw voice:join emit left the caller connected with no audio too).
      joinVoice(channelId, {
        onError: (msg) => {
          toast.error(msg);
          if (current.kind === 'connecting' && current.channelId === channelId) {
            set({ kind: 'idle' });
          }
        },
      });
    }
    return;
  }
  if (type === 'state') {
    // Once SFU is wired and we know voice is flowing, flip to in-call.
    if (current.kind === 'connecting' || current.kind === 'outgoing') {
      const channelId = typeof payload.channelId === 'string' ? payload.channelId : null;
      if (channelId && (current.kind === 'connecting' || current.kind === 'outgoing')
        && current.channelId === channelId) {
        set({ kind: 'in-call', channelId });
      }
    }
    return;
  }
}

/** Hung up locally during an active call. Fully leaves voice — clears the
 *  voice intent + tears down media (via leaveVoice) so the user is NOT
 *  auto-rejoined to whatever channel they sat in before the call. They go
 *  back to "not in any voice"; re-entering a server channel is manual. */
export function endActiveCall(): void {
  if (current.kind !== 'in-call' && current.kind !== 'connecting') return;
  leaveVoice();
  clearIncomingTimer();
  clearOutgoingTimer();
  set({ kind: 'idle' });
}

/**
 * We left the call from somewhere OTHER than the call UI — the voice sidebar's
 * «Отключиться», a channel switch, or a server-side force-disconnect. Voice
 * media is already down, so this only settles the call state machine; calling
 * leaveVoice() again would emit a second, pointless voice:leave.
 *
 * Without this the call UI stayed in "in-call" after hanging up from the
 * sidebar: the modal/bar kept showing an active call that no longer existed.
 */
export function noteSelfLeft(channelId: string): void {
  if (current.kind === 'idle') return;
  if ('channelId' in current && current.channelId !== channelId) return;
  clearIncomingTimer();
  clearOutgoingTimer();
  set({ kind: 'idle' });
}

/** The other side of a 1:1 call dropped out → end our side too (it's just
 *  the two of us). Called from the socket layer on a peer-left dm:state. */
export function endCallOnChannel(channelId: string): void {
  if (
    (current.kind === 'in-call' || current.kind === 'connecting') &&
    current.channelId === channelId
  ) {
    leaveVoice();
    clearIncomingTimer();
    clearOutgoingTimer();
    set({ kind: 'idle' });
  }
}
